import React, { FC } from 'react';

import { isAnnounced, OwnedInvestment as MyInvestment } from 'api-client';
import { CAPITAL_PROTECTION_REQUIRED_PROJECTS } from 'components/banners';
import {
  CapitalProtectionIcon,
  CustomLink,
  DataTable,
  Money,
  ProjectStatusBadge,
} from 'components/common';
import { ProjectDebtClass } from 'components/project';
import { useProfileData, useProjectRowClick } from 'hooks';
import { RouteDictionary } from 'utils';

import { useMyInvestmentContent } from '../useMyInvestmentContent';
import styles from './MyInvestmentTableRow.module.scss';

interface Props {
  investment: MyInvestment;
}

const MyInvestmentTableRow: FC<Props> = ({ investment }) => {
  const { project } = investment;
  const { roiIrr, progressBar, timeline } = useMyInvestmentContent(investment);
  const hasCapitalProtection = investment.investments.some((item) => item.capitalProtection);
  const { capitalProtectedInvestmentsCount } = useProfileData();
  const isCapitalProtectionActive =
    (capitalProtectedInvestmentsCount ?? 0) >= CAPITAL_PROTECTION_REQUIRED_PROJECTS;
  const handleRowClick = useProjectRowClick(project.uuid);

  if (isAnnounced(project.status)) return null;

  return (
    <DataTable.Row className={styles.row} onClick={handleRowClick}>
      <DataTable.Cell>
        <CustomLink
          to={RouteDictionary.DETAILED_PROJECT}
          params={{ uuid: project.uuid }}
          className={styles.projectLink}
        >
          <ProjectDebtClass debtClass={project.debtClass} />
          <div className={styles.projectName}>{project.name[0].text}</div>
          {project.hasExtraCreditorPayment && (
            <div className={styles.projectMeta}>
              <span className={styles.incasso}>инкассо</span>
            </div>
          )}
        </CustomLink>
      </DataTable.Cell>
      <DataTable.Cell align="center">
        <ProjectStatusBadge status={project.status} />
      </DataTable.Cell>
      <DataTable.Cell className={styles.nowrap}>
        <div className={styles.investedCell}>
          <Money money={investment.investedSum} compact />
          {hasCapitalProtection && (
            <CapitalProtectionIcon
              active={isCapitalProtectionActive}
              className={styles.protectionIcon}
            />
          )}
        </div>
      </DataTable.Cell>
      <DataTable.Cell className={styles.nowrap}>
        {investment.recoveryForecast ? <Money money={investment.recoveryForecast} compact /> : '—'}
      </DataTable.Cell>
      <DataTable.Cell className={styles.nowrap}>{roiIrr}</DataTable.Cell>
      <DataTable.Cell>{progressBar}</DataTable.Cell>
      <DataTable.Cell>{timeline}</DataTable.Cell>
    </DataTable.Row>
  );
};

export default MyInvestmentTableRow;
